import { Box, ListItem, styled } from '@mui/material';
import { GridFooter, useGridApiContext, useGridEvent } from '@mui/x-data-grid';
import LoadingSpinner from 'components/loading-spinner';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

export const SummaryRow = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  borderTop: `1px solid ${theme.palette.darkBlue.main}`,
}));

interface StyledSummaryCellProps {
  isLastItem?: boolean;
}

// Styled component for individual summary cells
const StyledSummaryCell = styled(ListItem, {
  shouldForwardProp: (props) => props !== 'isLastItem',
})<StyledSummaryCellProps>(({ theme, isLastItem }) => ({
  boxSizing: 'border-box', // Crucial for width calculation
  borderRight: isLastItem ? '' : `1px solid ${theme.palette.darkBlue.main}`,
  padding: '16px 10px',
  backgroundColor: theme.palette.beige.o50,
}));

export interface SummaryItem {
  value: number | string | null | undefined | React.ReactNode;
  type?: 'number' | 'string' | 'currency' | 'percent' | 'custom';
  hidden?: boolean;
  minimumFractionDigits?: number;
  roundingMode?:
    | 'ceil'
    | 'floor'
    | 'expand'
    | 'trunc'
    | 'halfCeil'
    | 'halfFloor'
    | 'halfExpand'
    | 'halfTrunc'
    | 'halfEven'
    | undefined;
}

type MuiGridFooterBaseProps = React.ComponentProps<typeof GridFooter>;

// Extend the base props with your custom props, making them OPTIONAL
export type DataGridSummaryProps = MuiGridFooterBaseProps & {
  summaryValues?: SummaryItem[]; // Use `null | undefined` to represent empty/hidden columns if they are part of the array.
};

const DataGridSummary: React.FC<DataGridSummaryProps> = ({ summaryValues, sx }) => {
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  //   const currentApiRef = apiRef!;
  const currentApiRef = useGridApiContext();
  const currentSummaryValues = summaryValues!;

  if (!currentApiRef || !currentApiRef.current || !currentApiRef.current.state.columns || !summaryValues) {
    return <LoadingSpinner />;
  }

  const updateWidths = useCallback(() => {
    const columnsState = currentApiRef.current?.state?.columns;

    if (columnsState?.lookup && columnsState?.orderedFields?.length > 0) {
      const { lookup, orderedFields } = columnsState;
      const newWidths = orderedFields.map((field) => lookup[field].computedWidth);
      setColumnWidths(newWidths);
    } else {
      setColumnWidths([]);
    }
  }, [currentApiRef]);

  useGridEvent(currentApiRef, 'columnResize', updateWidths);
  useGridEvent(currentApiRef, 'viewportInnerSizeChange', updateWidths); // Trigger updateWidths when internal data grid events are triggered

  useEffect(() => {
    updateWidths(); // initialize correct column widths
  }, [updateWidths]);

  // If column widths are not yet available from the effect, render null
  if (columnWidths.length === 0) {
    return null;
  }

  const widths = columnWidths;

  return (
    <SummaryRow sx={sx}>
      <StyledSummaryCell sx={{ width: widths[0] }}>
        <FormattedMessage id={'productionSystems.summary'} />
      </StyledSummaryCell>
      {currentSummaryValues.map((item, idx) => {
        const widthIdx = idx + 1; // Column index for widths array (0 is for the label)
        const isHidden = item.hidden;
        const isLastItemInList = idx === currentSummaryValues.length - 1; // Check if it's the very last data item

        // Add a check to prevent errors if summaryValues has more items than actual columns
        if (widthIdx >= widths.length) {
          console.warn(`Summary item at index ${idx} has no corresponding column width at widths index ${widthIdx}.`);
          return null; // Skip rendering this item
        }

        return (
          <StyledSummaryCell
            key={idx}
            className={isHidden ? 'hide' : undefined}
            isLastItem={isLastItemInList}
            sx={{ width: widths[widthIdx], justifyContent: 'right' }}>
            {/* Render content only if not hidden and value is not null/undefined */}
            {!isHidden && item.value !== null && item.value !== undefined ? (
              typeof item.value === 'number' ? (
                <FormattedNumber
                  value={item.value}
                  minimumFractionDigits={item.minimumFractionDigits}
                  roundingMode={item.roundingMode}
                />
              ) : (
                item.value
              )
            ) : null}
          </StyledSummaryCell>
        );
      })}
    </SummaryRow>
  );
};

export default React.memo(DataGridSummary);
